import _ from 'lodash';
import {connect} from 'react-redux';

import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../../../store/appState';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getStarredTagIds, getTags} from '../../../../store/data/tags/tagsSelectors';
import {ConversationUndoBannerState} from '../../../../store/panels/app/banners/bannersState';
import {
  undoConversationBannerAsyncAction,
  viewConversationBannerAction,
} from '../../../../store/panels/app/banners/undoBannersActions';
import {ConversationUndoBanner} from './conversationUndoBanner';

/*
 * Props.
 */

interface ConversationUndoBannerContainerProps {
  panelId: AppPanelId;
  state: ConversationUndoBannerState;
}

/*
 * Selectors.
 */

function getState(_state: AppState, props: ConversationUndoBannerContainerProps) {
  return props.state;
}

function makeGetTagProps() {
  return createSelector([getState, getTags, getStarredTagIds], (state, tags, starredTagIds) => {
    const newConversation = state.newConversations[0];
    const oldConversation = state.oldConversations[0];

    if (!newConversation || !oldConversation) {
      return undefined;
    }

    const added = _(newConversation.tagIds)
      .difference(oldConversation.tagIds)
      .uniq()
      .map((id) => tags[id])
      .compact()
      .first();
    const removed = _(oldConversation.tagIds)
      .difference(newConversation.tagIds)
      .uniq()
      .map((id) => tags[id])
      .compact()
      .first();

    if (!removed && !added) {
      return undefined;
    }

    return {
      added: {
        tag: added,
        isStarredTag: Boolean(added && starredTagIds.has(added.id)),
      },
      removed: {
        tag: removed,
        isStarredTag: Boolean(removed && starredTagIds.has(removed.id)),
      },
    };
  });
}

function makeGetInboxProps() {
  return createSelector([getState, getInboxes], (state, inboxes) => {
    const {newConversations, oldConversations} = state;
    const newInboxIds = _(newConversations)
      .flatMap((c) => c.inboxIds)
      .uniq()
      .sort()
      .value();
    const oldInboxIds = _(oldConversations)
      .flatMap((c) => c.inboxIds)
      .uniq()
      .sort()
      .value();

    if (_.isEqual(newInboxIds, oldInboxIds) || newInboxIds.length > 1) {
      return undefined;
    }

    const newInbox = inboxes[newInboxIds[0]];
    if (!newInbox) {
      return undefined;
    }

    return {
      inbox: newInbox,
    };
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationUndoBannerContainerProps>()(() => ({
  tagProps: makeGetTagProps(),
  inboxProps: makeGetInboxProps(),
}));

const mapDispatchToProps = makeMapDispatchToProps<ConversationUndoBannerContainerProps>()((props) => ({
  onUndoClick: () => undoConversationBannerAsyncAction(props.panelId, props.state),
  onViewClick: () => viewConversationBannerAction(props.state),
}));

export const ConversationUndoBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConversationUndoBanner);
